import * as Dialog from './Components.tsx';

import {
  createEffect,
  createSignal,
  JSX,
  JSXElement,
  mergeProps,
  Show
} from 'solid-js';
import { XIcon } from 'lucide-solid';
import { Button, ButtonProps } from '~/components/ui/Button';
import styles from './modal.module.scss';
import { getAppliedClasses } from '~/utils/commonUtils.ts';
import { ark } from '@ark-ui/solid';

export enum ModalTypes {
  FORM = 'form',
  DIALOG = 'dialog'
}

export enum ModalSizes {
  XXX_LARGE = 'xxxl',
  XX_LARGE = 'xxl',
  X_LARGE = 'custom_xl',
  LARGE = 'xl',
  MEDIUM = 'md',
  SMALL = 'sm'
}

type ModalProps = {
  title?: JSXElement;
  subTitle?: JSXElement;
  triggerComponent?:
    | JSXElement
    | ((props: JSX.HTMLAttributes<any>) => JSXElement);
  children?: JSXElement | ((closeModal: () => void) => JSXElement);
  customBackdrop?: JSXElement | ((closeModal: () => void) => JSXElement);
  primaryAction?: JSXElement | string;
  isPrimaryActionLoading?: boolean;
  secondaryAction?: JSXElement | string;
  onPrimaryAction?: (e: Event, closeModal: () => void) => void;
  onSecondaryAction?: () => void;
  open?: boolean;
  onOpenChange?: (isOpen: boolean) => void;
  type?: ModalTypes;
  size?: ModalSizes;
  lazyMount?: boolean;
  unmountOnExit?: boolean;
  showCloseIcon?: boolean;
  closeOnInteractOutside?: boolean;
  closeOnEscape?: boolean;
};

export default function Modal(props: ModalProps) {
  const merged: ModalProps = mergeProps(
    {
      isPrimaryActionLoading: false,
      onPrimaryAction: () => {},
      onSecondaryAction: () => {},
      type: ModalTypes.DIALOG,
      size: ModalSizes.MEDIUM,
      lazyMount: true,
      unmountOnExit: true,
      showCloseIcon: false,
      closeOnInteractOutside: false,
      closeOnEscape: false
    },
    props
  );
  const [openModal, setOpenModal] = createSignal(merged.open);

  createEffect(() => {
    setOpenModal(merged.open);
  });

  function closeModal() {
    setOpenModal(false);
  }

  return (
    <Dialog.Root
      {...merged}
      open={openModal()}
      onOpenChange={({ open }) => {
        props.onOpenChange && props.onOpenChange(open);
        setOpenModal(open);
      }}
      closeOnInteractOutside={merged.closeOnInteractOutside}
      closeOnEscape={merged.closeOnEscape}
    >
      <Show when={merged.triggerComponent}>
        <Dialog.Trigger
          asChild={(
            props: (
              userProps?: JSX.IntrinsicElements['button']
            ) => JSX.HTMLAttributes<any>
          ) => {
            return (
              <>
                {typeof merged.triggerComponent === 'function' ? (
                  merged.triggerComponent(props())
                ) : merged.triggerComponent ? (
                  <ark.div {...props()}>{merged.triggerComponent}</ark.div>
                ) : null}
              </>
            );
          }}
        />
      </Show>
      <Show when={merged.customBackdrop && openModal()}>
        <>
          {typeof merged.customBackdrop === 'function'
            ? merged.customBackdrop(closeModal)
            : merged.customBackdrop}
        </>
      </Show>
      <Show when={!merged.customBackdrop}>
        <Dialog.Backdrop />
      </Show>
      <Dialog.Positioner>
        <Dialog.Content
          class={`${getAppliedClasses({
            [styles.modalSm]: merged.size === ModalSizes.SMALL,
            [styles.modalMd]: merged.size === ModalSizes.MEDIUM,
            [styles.modalXl]: merged.size === ModalSizes.LARGE,
            [styles.modalCustomXl]: merged.size === ModalSizes.X_LARGE,
            [styles.modal2Xl]: merged.size === ModalSizes.XX_LARGE,
            [styles.modal3Xl]: merged.size === ModalSizes.XXX_LARGE
          })} ${styles.dialogContent}`}
        >
          <div class={`p-7 flex flex-col gap-2`}>
            <Show when={merged.title || merged.showCloseIcon}>
              <div class={`flex justify-between`}>
                <Show when={merged.title}>
                  <Dialog.Title class={'flex-grow'}>
                    {merged.title}
                  </Dialog.Title>
                </Show>
                <Show when={merged.showCloseIcon}>
                  <Dialog.CloseTrigger>
                    <Button
                      variant={`ghost`}
                      size={'xs'}
                      onClick={closeModal}
                    >
                      <XIcon />
                    </Button>
                  </Dialog.CloseTrigger>
                </Show>
              </div>
            </Show>
            <Dialog.Description>
              <Show when={merged.subTitle}>
                <p class={`w-full mt-1 text-sm`}>{merged.subTitle}</p>
              </Show>
              <div class={`w-full mt-6`}>
                {typeof merged.children === 'function'
                  ? merged.children(closeModal)
                  : merged.children}
              </div>
            </Dialog.Description>
            <Show
              when={
                merged.type === ModalTypes.DIALOG &&
                (merged.secondaryAction || merged.primaryAction)
              }
            >
              <div class={`flex justify-end mt-6`}>
                {merged.secondaryAction && (
                  <Dialog.CloseTrigger
                    class={`mr-2`}
                    asChild={(props) => (
                      <Button
                        {...(props() as ButtonProps)}
                        variant={`outline`}
                        size={'sm'}
                      >
                        {merged.secondaryAction}
                      </Button>
                    )}
                  />
                )}
                {merged.primaryAction && (
                  <Button
                    size={'sm'}
                    loading={merged.isPrimaryActionLoading}
                    onClick={(e) => {
                      merged.onPrimaryAction &&
                        merged.onPrimaryAction(e, closeModal);
                    }}
                  >
                    {merged.primaryAction}
                  </Button>
                )}
              </div>
            </Show>
          </div>
        </Dialog.Content>
      </Dialog.Positioner>
    </Dialog.Root>
  );
}
